import { buildPartMatchRulePayload, getPartMatchRuleGroups } from './partAutoSelectUtils';
import { getValueRuleGroups } from './configurationAdminUtils';

export const getPumpCellContextSelections = (source, stationId, cell, fieldIds) => {
  if (!source) return {};
  const keys = new Set((cell.ruleTargets || []).filter(target => target.fieldId === fieldIds.motorHp).flatMap(target => target.branchKeys));
  const branches = getValueRuleGroups(source.valueRules, stationId, new Set(cell.hpValueIds))
    .flatMap(group => group.values).filter(branch => keys.has(branch.key));
  if (!branches.length) return {};
  const commonIds = branches[0].dependencies.map(row => row.parentFieldValueId)
    .filter(id => branches.every(branch => branch.dependencies.some(row => row.parentFieldValueId === id)));
  const externalValues = source.fieldValues.filter(value => commonIds.includes(value.id) && !Object.values(fieldIds).includes(value.configFieldId));
  return Object.fromEntries(externalValues.filter(value => externalValues.filter(other => other.configFieldId === value.configFieldId).length === 1)
    .map(value => [value.configFieldId, value.id]));
};

// Discovery, not runtime matching: retain extra conditions for inspection.
export const isPartRuleGroupRelatedToCell = (group, context, values) => {
  const { cell, fieldIds, selections = {} } = context;
  const bounds = { [fieldIds.gpm]: [cell.gpmMin, cell.gpmMax], [fieldIds.dischargePsi]: [cell.psiMin, cell.psiMax] };
  return group.conditions.every(rule => {
    if (rule.requiredValueId) {
      const value = values.find(item => item.id === rule.requiredValueId);
      if (!value) return true;
      if (value.configFieldId === fieldIds.motorHp) return cell.hpValueIds.includes(value.id);
      if (selections[value.configFieldId]) return Number(selections[value.configFieldId]) === value.id;
      const range = bounds[value.configFieldId];
      return !range || (Number(value.minValue ?? value.optionValue) <= range[1] && Number(value.maxValue ?? value.optionValue) >= range[0]);
    }
    if (rule.fieldId === fieldIds.motorHp) return cell.hpValues.some(value => Number(value.label) >= (rule.minValue ?? -Infinity) && Number(value.label) <= (rule.maxValue ?? Infinity));
    const range = bounds[rule.fieldId];
    return !range || ((rule.minValue ?? -Infinity) <= range[1] && (rule.maxValue ?? Infinity) >= range[0]);
  });
};

export const getPumpAssignmentContextFields = (source, model, stationId, partRules = []) => {
  const performanceIds = new Set(Object.values(model.fieldIds || {}));
  const performanceValues = new Set(source.fieldValues.filter(value => performanceIds.has(value.configFieldId)).map(value => value.id));
  const dependencyIds = new Set(getValueRuleGroups(source.valueRules, stationId, performanceValues)
    .flatMap(group => group.values).flatMap(branch => branch.dependencies)
    .map(row => row.parentFieldValueId));
  partRules.forEach(rule => { if (rule.stationId === stationId && rule.requiredValueId) dependencyIds.add(rule.requiredValueId); });
  const contextIds = new Set(source.fieldValues.filter(value => dependencyIds.has(value.id)).map(value => value.configFieldId));
  const assignedIds = new Set(source.valueRules.filter(rule => rule.parentStationId === stationId).map(rule => rule.fieldValueId));
  return source.fields.filter(field => !performanceIds.has(field.id) && (
    contextIds.has(field.id) || (['pump description', 'type', 'pump type', 'configuration'].includes(field.fieldName.trim().toLowerCase()) && source.fieldValues.some(value => value.configFieldId === field.id && assignedIds.has(value.id)))
  ));
};

export const pumpCellConditions = (cell, fieldIds, selections) => {
  if (cell.hpValueIds.length !== 1) throw new Error('This cell must resolve to exactly one HP before assigning a pump. Refine the configuration context.');
  if (![cell.psiMin, cell.psiMax, cell.gpmMin, cell.gpmMax].every(Number.isFinite)) throw new Error('This cell does not have finite flow and pressure ranges.');
  return [
    { type: 'numeric', fieldId: fieldIds.gpm, minValue: cell.gpmMin, maxValue: cell.gpmMax },
    { type: 'numeric', fieldId: fieldIds.dischargePsi, minValue: cell.psiMin, maxValue: cell.psiMax },
    ...[...new Set([cell.hpValueIds[0], ...Object.values(selections).map(Number).filter(Boolean)])]
      .map(requiredValueId => ({ type: 'value', requiredValueId })),
  ];
};

const conditionKey = rule => JSON.stringify([rule.requiredValueId || null, rule.fieldId || null, rule.minValue ?? null, rule.maxValue ?? null]);
export const exactPumpAssignmentGroups = (rules, stationId, conditions) => {
  const expected = conditions.map(condition => conditionKey({ ...condition, minValue: condition.minValue ?? null, maxValue: condition.maxValue ?? null })).sort().join('|');
  return [...new Set(rules.filter(rule => rule.stationId === stationId).map(rule => rule.partId))]
    .flatMap(partId => getPartMatchRuleGroups(rules, partId, stationId).map(group => ({ ...group, partId })))
    .filter(group => group.conditions.map(conditionKey).sort().join('|') === expected);
};

export const buildPumpAssignmentPayloads = ({ cell, fieldIds, selections, stationId, partId, groupId, quantity = 1 }) => {
  if (!(stationId > 0 && partId > 0 && groupId > 0)) throw new Error('Station, part and rule group IDs are required.');
  return pumpCellConditions(cell, fieldIds, selections).map(condition => buildPartMatchRulePayload(partId, groupId, { ...condition, quantity }, 'api', stationId));
};

// Require full-cell coverage, not just a match at its midpoint or one corner.
export const matchingPumpAssignmentGroups = (rules, stationId, cell, fieldIds, selections, values) => {
  const selected = new Set([...Object.values(selections).map(Number), ...cell.hpValueIds]);
  const bounds = { [fieldIds.gpm]: [cell.gpmMin, cell.gpmMax], [fieldIds.dischargePsi]: [cell.psiMin, cell.psiMax] };
  if (cell.hpValues.length === 1 && Number.isFinite(Number(cell.hpValues[0].label))) bounds[fieldIds.motorHp] = [Number(cell.hpValues[0].label), Number(cell.hpValues[0].label)];
  return [...new Set(rules.filter(rule => rule.stationId === stationId).map(rule => rule.partId))]
    .flatMap(partId => getPartMatchRuleGroups(rules, partId, stationId).map(group => ({ ...group, partId })))
    .filter(group => group.conditions.every(rule => {
      if (rule.requiredValueId) {
        if (selected.has(rule.requiredValueId)) return true;
        const value = values.find(item => item.id === rule.requiredValueId);
        const range = bounds[value?.configFieldId];
        return range && Number(value.minValue ?? value.optionValue) <= range[0] && Number(value.maxValue ?? value.optionValue) >= range[1];
      }
      if (!rule.fieldId) return true;
      const range = bounds[rule.fieldId];
      return range && (rule.minValue === null || rule.minValue <= range[0]) && (rule.maxValue === null || rule.maxValue >= range[1]);
    }));
};
